import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Gift,
  Send,
  Loader2,
  CheckCircle,
  Heart,
  Copy,
  ArrowRight,
  Sparkles,
  Hand,
  Eye,
  Scissors,
  Waves,
  Droplets,
  BookOpen,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceGifts = [
  { category: "Facials", icon: Sparkles, items: [
    { name: "Classic Facial", price: 7000 },
    { name: "Deep Cleaning Facial", price: 11000 },
    { name: "Acne Facial", price: 11000 },
    { name: "LED Therapy Facial", price: 10000 },
    { name: "High Frequency Facial", price: 9000 },
    { name: "Nano Needling Facial", price: 14000 },
    { name: "Microdermabrasion Facial", price: 15000 },
    { name: "Mild Chemical Facial", price: 12000 },
    { name: "Back Facial", price: 8000 },
    { name: "Deep Cleaning Back Facial", price: 10000 },
    { name: "Brightening Back Facial", price: 13000 },
  ]},
  { category: "Nails", icon: Hand, items: [
    { name: "Gel-X Short", price: 8000 },
    { name: "Gel-X Medium", price: 10000 },
    { name: "Acrylic Nails Short", price: 7000 },
    { name: "Acrylic Nails Medium", price: 9000 },
  ]},
  { category: "Manicure & Pedicure", icon: Hand, items: [
    { name: "Spa Manicure", price: 5000 },
    { name: "Gel Manicure", price: 8000 },
    { name: "Basic Pedicure", price: 8000 },
    { name: "Jelly Pedicure", price: 10000 },
    { name: "Luxury Spa Pedicure", price: 12000 },
  ]},
  { category: "Lash Extensions", icon: Eye, items: [
    { name: "Hybrid Lashes", price: 10000 },
    { name: "Volume Lashes", price: 13000 },
    { name: "Classic Lashes", price: 15000 },
  ]},
  { category: "Waxing", icon: Droplets, items: [
    { name: "Full Face Waxing", price: 7000 },
    { name: "Half Leg Waxing", price: 7000 },
    { name: "Full Leg Waxing", price: 13500 },
    { name: "Brazilian Waxing", price: 14000 },
  ]},
  { category: "Massage", icon: Waves, items: [
    { name: "Swedish Massage (40 min)", price: 12000 },
    { name: "Aromatherapy Massage (40 min)", price: 13000 },
    { name: "Back Massage (30 min)", price: 7000 },
    { name: "Head, Neck, Face & Scalp Massage (30 min)", price: 8000 },
    { name: "Neck & Shoulder Massage (30 min)", price: 6000 },
    { name: "Wood Therapy Massage (60 min)", price: 15000 },
    { name: "Wood Therapy + Lymphatic Drainage Luxury Package (100 min)", price: 35000 },
  ]},
];

const trainingGifts = [
  { category: "Training Courses", icon: BookOpen, items: [
    { name: "Beginner Facial Course", price: 45000 },
    { name: "Advanced Facial Course", price: 55000 },
    { name: "Lip Care Product Making Course", price: 60000 },
    { name: "Beginners Nail Course", price: 40000 },
    { name: "Gel-X & Builder Gel Nail Course", price: 50000 },
    { name: "Acrylic Nails Course", price: 50000 },
    { name: "Advanced Nail Course", price: 50000 },
    { name: "Lash Extension Course", price: 60000 },
    { name: "Lash Lift Training Course", price: 45000 },
    { name: "Body Waxing Course", price: 60000 },
    { name: "Hair Services Course", price: 55000 },
    { name: "Advanced Hair Course", price: 60000 },
    { name: "Brow Training Course", price: 40000 },
    { name: "Wig Master Class", price: 80000 },
  ]},
  { category: "Special Courses", icon: Sparkles, items: [
    { name: "Beginners Candle Making Course", price: 45000 },
    { name: "Advanced Candle Making Course", price: 60000 },
    { name: "Soap Making Course", price: 60000 },
    { name: "Body Butter & Scrub Course", price: 60000 },
  ]},
];

export const GiftCardsPage = () => {
  const [step, setStep] = useState(1);
  const [giftType, setGiftType] = useState("service");
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_phone: "",
    buyer_email: "",
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    message: "",
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const selectItem = (item) => {
    setSelectedItem(item);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: selectedItem.price,
        gift_item: selectedItem.name,
        gift_type: giftType,
      };
      const res = await axios.post(`${API}/gift-cards`, payload);
      if (res.data.success) {
        setPurchaseResult(res.data);
        setStep(3);
        toast.success("Gift card purchased!");
      }
    } catch {
      toast.error("Failed to purchase gift card");
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(purchaseResult?.gift_card_code || "");
    toast.success("Gift card code copied!");
  };

  const currentCategories = giftType === "service" ? serviceGifts : trainingGifts;

  return (
    <div data-testid="gift-cards-page" className="pt-20">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Gift className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
              Digital<br />
              <span className="gold-gradient">Gift Cards</span>
            </h1>
            <p className="font-body text-lg text-[#A3A3A3] max-w-2xl mx-auto">
              Give the gift of luxury beauty. Choose a service or training course
              as a gift for someone special. Perfect for birthdays, holidays, or just because.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Step Indicator */}
      <section className="py-6 bg-[#0A0A0A] border-b border-[#262626]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center gap-2 md:gap-8">
            {[
              { num: 1, label: "Choose Gift" },
              { num: 2, label: "Details" },
              { num: 3, label: "Confirmation" },
            ].map((s) => (
              <div key={s.num} className="flex items-center gap-2 md:gap-3">
                <div className={`w-8 h-8 flex items-center justify-center text-sm font-semibold transition-all ${
                  step >= s.num ? "bg-[#C5A059] text-[#050505]" : "border border-[#262626] text-[#A3A3A3]"
                }`}>
                  {step > s.num ? <CheckCircle size={16} /> : s.num}
                </div>
                <span className={`font-body text-xs tracking-wider uppercase hidden sm:block ${
                  step >= s.num ? "text-white" : "text-[#A3A3A3]"
                }`}>{s.label}</span>
                {s.num < 3 && <div className={`w-8 md:w-16 h-px ${step > s.num ? "bg-[#C5A059]" : "bg-[#262626]"}`} />}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">

          {/* Step 1: Choose Gift */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              {/* Service / Training Toggle */}
              <div className="flex gap-3 mb-8">
                <button
                  onClick={() => { setGiftType("service"); setSelectedItem(null); setExpandedCategory(null); }}
                  data-testid="gift-type-service"
                  className={`flex-1 py-4 text-center font-semibold text-xs tracking-[0.15em] uppercase transition-all ${
                    giftType === "service"
                      ? "bg-[#C5A059] text-[#050505]"
                      : "bento-card text-[#A3A3A3] hover:text-white"
                  }`}
                >
                  Gift a Service
                </button>
                <button
                  onClick={() => { setGiftType("training"); setSelectedItem(null); setExpandedCategory(null); }}
                  data-testid="gift-type-training"
                  className={`flex-1 py-4 text-center font-semibold text-xs tracking-[0.15em] uppercase transition-all ${
                    giftType === "training"
                      ? "bg-[#C5A059] text-[#050505]"
                      : "bento-card text-[#A3A3A3] hover:text-white"
                  }`}
                >
                  Gift a Training Course
                </button>
              </div>

              {/* Categories */}
              <div className="space-y-3">
                {currentCategories.map((cat) => {
                  const Icon = cat.icon;
                  const isExpanded = expandedCategory === cat.category;
                  return (
                    <div key={cat.category} className="bento-card overflow-hidden">
                      <button
                        onClick={() => setExpandedCategory(isExpanded ? null : cat.category)}
                        data-testid={`gift-category-${cat.category.toLowerCase().replace(/\s+/g, '-')}`}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-[#1a1a1a]/50 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 border border-[#C5A059]/30 flex items-center justify-center">
                            <Icon size={18} className="text-[#C5A059]" />
                          </div>
                          <span className="font-body text-sm font-semibold text-white tracking-wide uppercase">{cat.category}</span>
                          <span className="text-xs text-[#A3A3A3]">({cat.items.length})</span>
                        </div>
                        <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                          <ArrowRight size={16} className="text-[#A3A3A3] rotate-90" />
                        </motion.div>
                      </button>

                      {isExpanded && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                          className="border-t border-[#262626]">
                          <div className="p-3 space-y-1">
                            {cat.items.map((item) => {
                              const isSelected = selectedItem?.name === item.name;
                              return (
                                <button
                                  key={item.name}
                                  onClick={() => selectItem(item)}
                                  data-testid={`gift-item-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                  className={`w-full flex items-center justify-between p-4 text-left transition-all ${
                                    isSelected
                                      ? "bg-[#C5A059]/10 border border-[#C5A059]"
                                      : "hover:bg-[#1a1a1a] border border-transparent"
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-4 h-4 border-2 flex items-center justify-center transition-all ${
                                      isSelected ? "border-[#C5A059] bg-[#C5A059]" : "border-[#A3A3A3]"
                                    }`}>
                                      {isSelected && <CheckCircle size={10} className="text-[#050505]" />}
                                    </div>
                                    <span className={`text-sm ${isSelected ? "text-white" : "text-[#A3A3A3]"}`}>{item.name}</span>
                                  </div>
                                  <span className={`font-heading text-lg ${isSelected ? "text-[#C5A059]" : "text-[#A3A3A3]"}`}>
                                    ${item.price.toLocaleString()} <span className="text-xs font-body">GYD</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Selected Item Preview */}
              {selectedItem && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                  <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#C5A059]/30 p-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A059]/5 rounded-full -mr-16 -mt-16" />
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-4">
                        <Gift className="w-8 h-8 text-[#C5A059]" />
                        <span className="font-heading text-lg text-white">Luxury Beauty Gift Card</span>
                      </div>
                      <p className="font-body text-sm text-[#C5A059] uppercase tracking-wider mb-2">
                        {giftType === "service" ? "Service Gift" : "Training Gift"}
                      </p>
                      <h3 className="font-heading text-xl text-white mb-2">{selectedItem.name}</h3>
                      <span className="font-heading text-3xl text-[#C5A059]">${selectedItem.price.toLocaleString()}</span>
                      <span className="font-body text-sm text-[#A3A3A3] ml-2">GYD</span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div className="mt-8">
                <button
                  onClick={() => { if (selectedItem) { setStep(2); window.scrollTo(0, 0); } }}
                  disabled={!selectedItem}
                  data-testid="gift-next-details"
                  className="bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase px-8 py-4 hover:brightness-110 transition-all disabled:opacity-30"
                >
                  Next: Add Recipient Details
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Details */}
          {step === 2 && selectedItem && (
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <button onClick={() => setStep(1)} className="text-[#C5A059] text-sm hover:underline mb-6 block">
                &larr; Change selection
              </button>

              <div className="bg-[#121212] border border-[#C5A059]/20 p-4 mb-8 flex items-center gap-4">
                <Gift className="w-8 h-8 text-[#C5A059] flex-shrink-0" />
                <div>
                  <span className="font-heading text-lg text-white">{selectedItem.name}</span>
                  <span className="block text-sm text-[#C5A059]">${selectedItem.price.toLocaleString()} GYD — {giftType === "service" ? "Service" : "Training Course"}</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Buyer Info */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">Your Information (Buyer)</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Name *</label>
                      <input type="text" name="buyer_name" value={form.buyer_name} onChange={handleChange} required
                        data-testid="gift-buyer-name" placeholder="Your name" className="w-full luxury-input" />
                    </div>
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Phone *</label>
                      <input type="tel" name="buyer_phone" value={form.buyer_phone} onChange={handleChange} required
                        data-testid="gift-buyer-phone" placeholder="Phone number" className="w-full luxury-input" />
                    </div>
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Email</label>
                      <input type="email" name="buyer_email" value={form.buyer_email} onChange={handleChange}
                        data-testid="gift-buyer-email" placeholder="Email (optional)" className="w-full luxury-input" />
                    </div>
                  </div>
                </div>

                {/* Recipient Info */}
                <div className="bg-[#121212] border border-[#262626] p-6">
                  <h3 className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059] mb-4">
                    <Heart size={14} className="inline mr-2" /> Recipient Information
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Recipient Name *</label>
                      <input type="text" name="recipient_name" value={form.recipient_name} onChange={handleChange} required
                        data-testid="gift-recipient-name" placeholder="Who is this for?" className="w-full luxury-input" />
                    </div>
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Recipient Phone</label>
                      <input type="tel" name="recipient_phone" value={form.recipient_phone} onChange={handleChange}
                        data-testid="gift-recipient-phone" placeholder="Recipient's phone (optional)" className="w-full luxury-input" />
                    </div>
                    <div>
                      <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Recipient Email</label>
                      <input type="email" name="recipient_email" value={form.recipient_email} onChange={handleChange}
                        data-testid="gift-recipient-email" placeholder="Recipient's email (optional)" className="w-full luxury-input" />
                    </div>
                  </div>
                  <div className="mt-6">
                    <label className="block font-body text-xs tracking-wider uppercase text-[#A3A3A3] mb-2">Personal Message</label>
                    <textarea name="message" value={form.message} onChange={handleChange} rows={3}
                      data-testid="gift-message" placeholder="Add a personal message (optional)..."
                      className="w-full luxury-input resize-none" maxLength={300} />
                    <p className="text-[10px] text-[#A3A3A3] mt-1">{form.message.length}/300</p>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={!form.buyer_name || !form.buyer_phone || !form.recipient_name || isSubmitting}
                  data-testid="gift-purchase-btn"
                  className="w-full bg-[#C5A059] text-[#050505] font-semibold text-sm tracking-[0.15em] uppercase py-4 hover:brightness-110 transition-all disabled:opacity-40 flex items-center justify-center gap-3"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                  ) : (
                    <><Gift className="w-5 h-5" /> Purchase Gift Card — {selectedItem.name} (${selectedItem.price.toLocaleString()} GYD)</>
                  )}
                </button>

                <p className="font-body text-xs text-[#A3A3A3] text-center">
                  By purchasing, you agree to our <Link to="/gift-card-policy" className="text-[#C5A059] hover:underline">Gift Card Policy</Link>.
                </p>

                {/* MMG Payment Notice */}
                <div className="mt-4 bg-[#121212] border border-[#C5A059]/20 p-4 flex items-start gap-3">
                  <Gift size={18} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-[#C5A059] text-xs font-semibold tracking-wider uppercase block mb-1">Pay via MMG</span>
                    <p className="text-[#A3A3A3] text-xs leading-relaxed">
                      Merchant Name: <strong className="text-white">LB Beauty</strong>. Send your payment receipt to confirm your gift card purchase.
                    </p>
                  </div>
                </div>
              </form>
            </motion.div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && purchaseResult && selectedItem && (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
              <div className="bg-[#121212] border border-[#262626] p-8 md:p-12 text-center">
                <CheckCircle className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
                <h3 className="font-heading text-2xl md:text-3xl text-white mb-4">
                  Gift Card Created
                </h3>

                {/* Gift Card Display */}
                <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border border-[#C5A059]/30 p-8 my-8 relative overflow-hidden max-w-md mx-auto">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C5A059]/5 rounded-full -mr-16 -mt-16" />
                  <div className="relative text-left">
                    <div className="flex items-center gap-3 mb-4">
                      <Gift className="w-6 h-6 text-[#C5A059]" />
                      <span className="font-heading text-sm text-white">Luxury Beauty Gift Card</span>
                    </div>
                    <p className="text-xs text-[#C5A059] uppercase tracking-wider mb-1">
                      {giftType === "service" ? "Service Gift" : "Training Gift"}
                    </p>
                    <h4 className="font-heading text-lg text-white mb-2">{selectedItem.name}</h4>
                    <span className="font-heading text-3xl text-[#C5A059]">${selectedItem.price.toLocaleString()}</span>
                    <span className="text-sm text-[#A3A3A3] ml-2">GYD</span>
                    <div className="mt-4 pt-4 border-t border-[#262626]">
                      <p className="text-xs text-[#A3A3A3] mb-1">Gift Card Code</p>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-xl text-white" data-testid="gift-card-code">
                          {purchaseResult.gift_card_code}
                        </span>
                        <button onClick={copyCode} className="text-[#C5A059] hover:text-white transition-colors" data-testid="copy-gift-code-btn">
                          <Copy size={16} />
                        </button>
                      </div>
                    </div>
                    {form.recipient_name && (
                      <p className="text-xs text-[#A3A3A3] mt-3">For: <span className="text-white">{form.recipient_name}</span></p>
                    )}
                    {form.message && (
                      <p className="text-xs text-[#A3A3A3] mt-1 italic">"{form.message}"</p>
                    )}
                  </div>
                </div>

                <p className="font-body text-sm text-[#A3A3A3] mb-2">
                  We will contact you at <strong className="text-white">{form.buyer_phone}</strong> to arrange payment.
                </p>
                <p className="font-body text-xs text-[#A3A3A3] mb-8">
                  Share the code with {form.recipient_name} to redeem at Luxury Beauty.
                </p>

                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => { setStep(1); setSelectedItem(null); setForm({ buyer_name: "", buyer_phone: "", buyer_email: "", recipient_name: "", recipient_email: "", recipient_phone: "", message: "" }); setPurchaseResult(null); }}
                    data-testid="buy-another-btn"
                    className="text-[#C5A059] text-sm tracking-wider uppercase hover:underline"
                  >
                    Buy Another
                  </button>
                  <Link to="/events" className="text-[#A3A3A3] text-sm tracking-wider uppercase hover:text-white">
                    View Events
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Policy Link */}
      <section className="py-8 bg-[#050505] text-center">
        <Link to="/gift-card-policy" className="font-body text-sm text-[#A3A3A3] hover:text-[#C5A059] transition-colors">
          View Gift Card Policy <ArrowRight size={12} className="inline ml-1" />
        </Link>
      </section>
    </div>
  );
};

export default GiftCardsPage;
